<template>
  <div :class="embedded ? 'scope-tab-embedded' : 'scope-tab-shell plan-tab-scroll-area'">
    <div class="scope-tab-inner p-4">
    <div v-if="!subProjectId" class="text-secondary text-sm mb-3">
      No sub-project linked to this plan yet. You can still draft scope text below.
    </div>
    <div>
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h6 class="mb-0 font-weight-bold" style="font-size: 0.95rem">
          <i class="fas fa-clipboard-list text-info me-2"></i>{{ enableAi ? 'Test Scope' : 'Scope' }}
        </h6>
        <div v-if="!readOnly" class="d-flex gap-2">
          <button
            v-if="enableAi"
            type="button"
            class="btn btn-sm btn-outline-info mb-0 py-1 px-3"
            style="font-size: 0.75rem"
            :disabled="scopeSaving || aiLoading"
            @click="openAiModal"
          >
            <i class="fas fa-wand-magic-sparkles me-1" v-if="!aiLoading"></i>
            <i class="fas fa-spinner fa-spin me-1" v-else></i>AI Scope
          </button>
          <button
            type="button"
            class="btn btn-sm bg-gradient-dark text-white mb-0 py-1 px-3"
            style="font-size: 0.75rem"
            :disabled="scopeSaving || !subProjectId"
            @click="saveScope"
          >
            <i class="fas fa-spinner fa-spin me-1" v-if="scopeSaving"></i>
            <i class="fas fa-save me-1" v-else></i>Save
          </button>
        </div>
      </div>
      <textarea
        v-model="scopeText"
        class="form-control"
        rows="12"
        placeholder="Enter test scope..."
        style="font-size: 0.85rem; resize: vertical; font-family: inherit; line-height: 1.7"
        :readonly="readOnly"
      />
      <div v-if="scopeSaveError" class="text-danger text-xs mt-2">
        <i class="fas fa-exclamation-circle me-1"></i>{{ scopeSaveError }}
      </div>
    </div>

    <hr class="my-3" style="border-color: #e9ecef" />

    <!-- Document -->
    <div class="mb-2">
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 font-weight-bold" style="font-size: 0.95rem">
          <i class="fas fa-file-alt text-secondary me-2"></i>Document
        </h6>
      </div>

      <div v-if="!readOnly" class="scope-doc-link-row mb-3">
        <input
          v-model="documentLinkUrl"
          type="url"
          class="form-control form-control-sm scope-doc-link-input"
          placeholder="Paste destination link (https://...)"
          style="font-size: 0.8rem"
          :disabled="!subProjectId || documentLinkSaving"
          @keyup.enter="saveDocumentLink"
        />
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mb-0 py-1 px-3 scope-doc-action-btn"
          style="font-size: 0.75rem"
          :disabled="!subProjectId || !documentLinkUrl.trim() || documentLinkSaving"
          @click="saveDocumentLink"
        >
          <i class="fas fa-spinner fa-spin me-1" v-if="documentLinkSaving"></i>
          <i class="fas fa-link me-1" v-else></i>Add Link
        </button>
        <label
          class="btn btn-sm btn-outline-secondary mb-0 py-1 px-3 scope-doc-action-btn"
          style="font-size: 0.75rem; cursor: pointer"
          :class="{ disabled: !subProjectId || documentUploading }"
        >
          <i class="fas fa-spinner fa-spin me-1" v-if="documentUploading"></i>
          <i class="fas fa-file-import me-1" v-else></i>Import File
          <input
            type="file"
            class="d-none"
            :disabled="!subProjectId || documentUploading"
            @change="uploadDocumentFile"
          />
        </label>
      </div>

      <div
        v-if="scopeDocuments.files.length === 0 && scopeDocuments.links.length === 0"
        class="text-secondary text-sm py-3 text-center"
        style="background: #f8f9fa; border-radius: 8px; border: 1px dashed #dee2e6"
      >
        <i class="fas fa-file-alt me-1 opacity-5"></i>
        No documents yet. Import a file or add a destination link.
      </div>
      <div v-else class="scope-doc-list">
        <div v-for="file in scopeDocuments.files" :key="file.id" class="scope-doc-item">
          <div class="scope-doc-item-main min-width-0">
            <i :class="documentFileIcon(file)" class="scope-doc-type-icon"></i>
            <span class="scope-doc-name text-truncate">{{ file.name }}</span>
          </div>
          <div class="scope-doc-item-actions">
            <span class="scope-doc-date text-xs text-secondary">{{ formatDocDate(file.uploadedAt) }}</span>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 scope-doc-icon-btn"
              title="Quick View"
              @click="openDocumentPreview(file)"
            >
              <i class="fas fa-eye"></i>
            </button>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 scope-doc-icon-btn"
              title="Download"
              :disabled="docDownloadingId === file.id"
              @click="downloadDocumentFile(file)"
            >
              <i class="fas" :class="docDownloadingId === file.id ? 'fa-spinner fa-spin' : 'fa-download'"></i>
            </button>
            <button
              v-if="!readOnly"
              type="button"
              class="btn btn-link btn-sm p-0 action-delete"
              title="Remove file"
              :disabled="documentDeletingId === file.id"
              @click="removeDocument('file', file.id)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
        <div v-for="link in scopeDocuments.links" :key="link.id" class="scope-doc-item">
          <div class="scope-doc-item-main min-width-0">
            <i class="fas fa-link text-info me-2"></i>
            <a :href="link.url" target="_blank" rel="noopener noreferrer" class="scope-doc-name">{{ link.label || link.url }}</a>
            <span v-if="link.label" class="text-xs text-secondary ms-2 text-truncate">{{ link.url }}</span>
          </div>
          <button
            v-if="!readOnly"
            type="button"
            class="btn btn-link btn-sm p-0 action-delete"
            title="Remove link"
            :disabled="documentDeletingId === link.id"
            @click="removeDocument('link', link.id)"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </div>
      <div v-if="documentError" class="text-danger text-xs mt-2">
        <i class="fas fa-exclamation-circle me-1"></i>{{ documentError }}
      </div>
    </div>

    <hr class="my-3" style="border-color: #e9ecef" />

    <div class="mb-2">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h6 class="mb-0 font-weight-bold" style="font-size: 0.95rem">
          <i class="fab fa-jira text-primary me-2"></i>Jira Links
        </h6>
        <button
          v-if="!readOnly"
          type="button"
          class="btn btn-sm btn-outline-primary mb-0 py-1 px-3"
          style="font-size: 0.75rem"
          :disabled="!subProjectId"
          @click="openAddJiraModal"
        >
          <i class="fas fa-plus me-1"></i>Add Jira Link
        </button>
      </div>
      <p v-if="scopeJiraProjectKey" class="text-xs text-secondary mb-2">
        <i class="fab fa-jira me-1"></i>Jira Space: <strong>{{ scopeJiraProjectLabel }}</strong>
      </p>
      <div
        v-if="scopeJiraLinks.length === 0"
        class="text-secondary text-sm py-3 text-center"
        style="background: #f8f9fa; border-radius: 8px; border: 1px dashed #dee2e6"
      >
        <i class="fab fa-jira me-1 opacity-5"></i>
        <span v-if="scopeJiraProjectKey">No Jira tickets linked yet</span>
        <span v-else>No Jira links yet</span>
      </div>
      <div v-else class="table-responsive">
        <table class="table table-sm align-items-center mb-0 jira-links-table">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Jira ID</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 jira-title-col">Jira Title</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 jira-status-col">Jira Status</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center jira-action-col">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="j in scopeJiraLinks" :key="j.key">
              <td class="text-sm jira-id-cell">
                <button
                  type="button"
                  class="btn btn-link p-0 mb-0 font-weight-bold text-primary"
                  style="text-decoration:none;"
                  @click="openJiraView(j)"
                >
                  {{ j.key }}
                </button>
              </td>
              <td class="text-sm text-secondary jira-title-cell">{{ j.summary || "-" }}</td>
              <td class="text-sm jira-status-cell">
                <span v-if="readOnly" class="text-xs text-secondary">{{ j.status || "—" }}</span>
                <jira-status-select
                  v-else
                  :issue-key="j.key"
                  :status="j.status || ''"
                  :transitions="j.transitions || []"
                  compact
                  @update:status="j.status = $event"
                  @update:transitions="j.transitions = $event"
                />
              </td>
              <td class="text-center jira-action-cell">
                <button type="button" class="btn btn-link btn-sm p-0 action-view me-2" title="View" @click="openJiraView(j)">
                  <i class="fas fa-eye"></i>
                </button>
                <button
                  v-if="!readOnly"
                  type="button"
                  class="btn btn-link btn-sm p-0 action-delete"
                  @click="removeScopeJiraLink(j.key)"
                  title="Remove Jira link"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="jiraStatusError" class="text-danger text-xs mt-2">
          <i class="fas fa-exclamation-circle me-1"></i>{{ jiraStatusError }}
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showJiraViewModal" class="modal-overlay">
        <div class="modal-box scope-jira-view-box">
          <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">
              <i class="fab fa-jira text-primary me-1"></i>{{ jiraViewIssue?.key || "Jira Issue" }}
            </h6>
            <button type="button" class="scope-modal-close-x" title="Close" @click="closeJiraView">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="p-3 scope-jira-view-body">
            <div v-if="jiraViewLoading" class="text-center py-4 text-secondary text-sm">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading issue...
            </div>
            <template v-else-if="jiraViewIssue">
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Summary</label>
                <input :value="jiraViewIssue.summary || '—'" type="text" class="form-control form-control-sm" disabled />
              </div>
              <div class="row">
                <div class="col-4 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Status</label>
                  <jira-status-select
                    :issue-key="jiraViewIssue.key"
                    :status="jiraViewIssue.status || ''"
                    :transitions="jiraViewIssue.transitions || []"
                    :disabled="readOnly"
                    @update:status="onJiraViewStatusUpdate"
                    @update:transitions="onJiraViewTransitionsUpdate"
                    @error="onJiraViewError"
                  />
                </div>
                <div class="col-4 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Issue Type</label>
                  <input :value="jiraViewIssue.issueType || '—'" type="text" class="form-control form-control-sm" disabled />
                </div>
                <div class="col-4 mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Assignee</label>
                  <base-select
                    size="sm"
                    searchable
                    :model-value="jiraViewIssue.assigneeAccountId || ''"
                    :options="jiraViewAssigneeOptions"
                    :disabled="readOnly || jiraViewAssigneeSaving"
                    :placeholder="jiraViewIssue.assignee || 'Unassigned'"
                    @update:model-value="onJiraViewAssigneeChange"
                  />
                  <div v-if="jiraViewAssigneeSaving" class="text-xs text-secondary mt-1">
                    <i class="fas fa-spinner fa-spin me-1"></i>Updating assignee...
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Description</label>
                <div class="scope-jira-view-desc">{{ jiraViewIssue.description || "No description." }}</div>
              </div>
              <div class="mb-0">
                <label class="text-xs font-weight-bold mb-1 d-block">
                  Comments ({{ (jiraViewIssue.comments || []).length }})
                </label>
                <div v-if="!(jiraViewIssue.comments || []).length" class="scope-jira-view-empty text-xs text-secondary">
                  No comments.
                </div>
                <div v-else class="scope-jira-view-comments">
                  <div
                    v-for="comment in jiraViewIssue.comments"
                    :key="comment.id"
                    class="scope-jira-view-comment"
                  >
                    <div class="d-flex justify-content-between gap-2 mb-1">
                      <span class="font-weight-bold">{{ comment.author || "Unknown" }}</span>
                      <span class="text-secondary">{{ formatJiraCommentTime(comment.created) }}</span>
                    </div>
                    <div class="scope-jira-view-comment-body">{{ comment.body || "—" }}</div>
                  </div>
                </div>
                <div v-if="!readOnly" class="scope-jira-view-composer mt-3">
                  <textarea
                    v-model="jiraViewComment"
                    class="form-control form-control-sm"
                    rows="3"
                    placeholder="Write a comment as your connected Jira account..."
                    :disabled="jiraViewCommentSaving"
                    @keydown.meta.enter.prevent="submitJiraViewComment"
                    @keydown.ctrl.enter.prevent="submitJiraViewComment"
                  ></textarea>
                  <div class="d-flex justify-content-end mt-2">
                    <button
                      type="button"
                      class="btn btn-sm bg-gradient-dark text-white mb-0"
                      :disabled="jiraViewCommentSaving || !String(jiraViewComment || '').trim()"
                      @click="submitJiraViewComment"
                    >
                      <i class="fas fa-spinner fa-spin me-1" v-if="jiraViewCommentSaving"></i>
                      Comment
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="jiraViewError" class="text-danger text-xs mt-2">{{ jiraViewError }}</div>
          </div>
          <div class="d-flex justify-content-end p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeJiraView">Close</button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showSaveSuccessModal" class="modal-overlay">
        <div class="modal-box scope-confirm-modal">
          <div class="p-4 text-center">
            <i class="fas fa-check-circle text-success fa-2x mb-3"></i>
            <h6 class="font-weight-bold mb-2">Saved successfully</h6>
            <p class="text-sm text-secondary mb-0">Test Scope has been saved.</p>
          </div>
          <div class="d-flex justify-content-center p-3 border-top">
            <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0 px-4" @click="showSaveSuccessModal = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showUnsavedModal" class="modal-overlay">
        <div class="modal-box scope-confirm-modal">
          <div class="scope-modal-header">
            <h6 class="font-weight-bold mb-0">Unsaved changes</h6>
            <button
              type="button"
              class="scope-modal-close-x"
              title="Close"
              @click="cancelUnsavedLeave"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="p-4 pt-3">
            <p class="text-sm text-secondary mb-0">
              Test Scope has unsaved changes. Save before leaving this tab?
            </p>
            <div v-if="unsavedSaveError" class="text-danger text-xs mt-2">{{ unsavedSaveError }}</div>
          </div>
          <div class="scope-modal-actions px-3 py-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-danger mb-0" @click="confirmUnsavedLeave">
              Leave without saving
            </button>
            <button
              type="button"
              class="btn btn-sm bg-gradient-dark text-white mb-0"
              :disabled="scopeSaving"
              @click="saveAndConfirmLeave"
            >
              <i class="fas fa-spinner fa-spin me-1" v-if="scopeSaving"></i>
              Save
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showAddJira" class="modal-overlay">
        <div class="modal-box" style="width: 480px">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Add Jira Link</h6>
          </div>
          <div class="p-3">
            <label class="text-xs font-weight-bold mb-1 d-block">Jira Space</label>
            <base-select
              size="sm"
              v-model="addJiraProjectKey"
              :options="jiraProjectSelectOptions"
              option-label="label"
              option-value="value"
              placeholder="— Select Space —"
              @change="onAddJiraProjectChange"
            />
            <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
            </span>
            <div v-if="addJiraProjectKey" class="mt-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Jira Tickets <span class="text-secondary font-weight-normal">(optional)</span></label>
              <div class="d-flex flex-wrap gap-1 mb-2" v-if="addJiraIssueKeys.length > 0">
                <span v-for="key in addJiraIssueKeys" :key="key" class="jira-tag">
                  <i class="fab fa-jira me-1"></i>{{ key }}
                  <button type="button" class="jira-tag-remove" @click="removeAddJiraTicket(key)">×</button>
                </span>
              </div>
              <input
                type="text"
                class="form-control form-control-sm mb-1"
                v-model="jiraSearch"
                placeholder="Search ticket..."
                @input="onJiraSearch"
              />
              <div class="jira-issue-list">
                <div v-if="jiraLoadingIssues" class="text-center py-2 text-secondary text-xs">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                </div>
                <div v-else-if="jiraIssues.length === 0" class="text-center py-2 text-secondary text-xs">No tickets found</div>
                <div
                  v-for="i in jiraIssues"
                  :key="i.key"
                  class="jira-issue-item"
                  :class="{ 'jira-issue-selected': addJiraIssueKeys.includes(i.key) }"
                  @click="toggleAddJiraTicket(i.key)"
                >
                  <i class="fas fa-check-circle me-1 text-success" v-if="addJiraIssueKeys.includes(i.key)"></i>
                  <i class="fas fa-circle me-1" style="font-size:0.5rem; color:transparent;" v-else></i>
                  <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
                  <span class="text-xs">{{ i.summary }}</span>
                  <span v-if="i.status" class="text-xs text-secondary ms-1">({{ i.status }})</span>
                </div>
              </div>
            </div>
            <div v-if="addJiraError" class="text-danger text-xs mt-2">{{ addJiraError }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeAddJiraModal">Cancel</button>
            <button type="button" class="btn btn-sm bg-gradient-primary text-white mb-0" @click="addScopeJiraLink">Add</button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="docPreview.show" class="modal-overlay">
        <div class="modal-box scope-doc-preview-box">
          <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold text-truncate pe-3">{{ docPreview.file?.name || "Preview" }}</h6>
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="closeDocPreview">Close</button>
          </div>
          <div class="scope-doc-preview-body">
            <div v-if="docPreview.loading" class="text-center py-5 text-secondary">
              <i class="fas fa-spinner fa-spin fa-lg"></i>
              <p class="text-sm mt-2 mb-0">Loading preview...</p>
            </div>
            <div v-else-if="docPreview.error" class="text-center py-5 px-3">
              <p class="text-danger text-sm mb-3">{{ docPreview.error }}</p>
              <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="openDocumentInNewTab(docPreview.file)">
                Open in new tab
              </button>
            </div>
            <iframe
              v-else-if="docPreview.kind === 'pdf'"
              :src="docPreview.url"
              class="scope-doc-preview-frame"
              title="Document preview"
            ></iframe>
            <div v-else-if="docPreview.kind === 'image'" class="scope-doc-preview-image-wrap">
              <img :src="docPreview.url" :alt="docPreview.file?.name || 'Preview'" class="scope-doc-preview-image" />
            </div>
            <pre v-else-if="isTextPreviewKind(docPreview.kind)" class="scope-doc-preview-text">{{ docPreview.textContent }}</pre>
            <div v-else class="text-center py-5 px-3">
              <i class="fas fa-file-alt fa-2x text-secondary opacity-5 mb-3"></i>
              <p class="text-secondary text-sm mb-3">Quick preview is not available for this file type.</p>
              <button type="button" class="btn btn-sm btn-outline-primary mb-0 me-2" @click="openDocumentInNewTab(docPreview.file)">
                Open in new tab
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="downloadDocumentFile(docPreview.file)">
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showAiModal" class="modal-overlay">
        <div class="modal-box" style="width: 560px">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Generate Scope by AI</h6>
          </div>
          <div class="p-3">
            <p class="text-xs text-secondary mb-2">
              AI will analyze all available sources and generate a detailed bilingual Test Scope (Thai + English) for writing test cases, scenarios, and sub-scenarios.
              Analysis may take 1–3 minutes.
            </p>
            <div class="scope-ai-sources mb-3">
              <div class="text-xs font-weight-bold mb-1">Sources to analyze</div>
              <ul class="scope-ai-source-list text-xs mb-0">
                <li>
                  <label class="scope-ai-source-item" :class="{ 'is-disabled': !hasCurrentScopeSource }">
                    <input
                      v-model="aiIncludeCurrentScope"
                      type="checkbox"
                      :disabled="!hasCurrentScopeSource"
                    />
                    <span>
                      Current Test Scope
                      <span v-if="hasCurrentScopeSource" class="text-secondary">
                        ({{ scopeText.trim().length }} chars — will be backed up as .txt)
                      </span>
                    </span>
                  </label>
                </li>
                <li>
                  <label class="scope-ai-source-item" :class="{ 'is-disabled': !hasFileSource }">
                    <input
                      v-model="aiIncludeFiles"
                      type="checkbox"
                      :disabled="!hasFileSource"
                    />
                    <span>Imported files ({{ scopeDocuments.files.length }})</span>
                  </label>
                </li>
                <li>
                  <label class="scope-ai-source-item" :class="{ 'is-disabled': !hasLinkSource }">
                    <input
                      v-model="aiIncludeLinks"
                      type="checkbox"
                      :disabled="!hasLinkSource"
                    />
                    <span>Document links ({{ scopeDocuments.links.length }})</span>
                  </label>
                </li>
                <li>
                  <label class="scope-ai-source-item" :class="{ 'is-disabled': !hasJiraSource }">
                    <input
                      v-model="aiIncludeJira"
                      type="checkbox"
                      :disabled="!hasJiraSource"
                    />
                    <span>Jira links ({{ scopeJiraLinks.length }})</span>
                  </label>
                </li>
              </ul>
            </div>
            <label class="text-xs font-weight-bold mb-1 d-block">Additional Requirement Text (optional)</label>
            <textarea
              v-model="aiInputText"
              class="form-control form-control-sm mb-2"
              rows="4"
              placeholder="Paste extra requirements here..."
            ></textarea>
            <label class="text-xs font-weight-bold mb-1 d-block">Or Additional URL (optional)</label>
            <input
              v-model="aiInputUrl"
              type="text"
              class="form-control form-control-sm"
              placeholder="https://..."
            />
            <div v-if="aiError" class="text-danger text-xs mt-2">{{ aiError }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="showAiModal = false">Cancel</button>
            <button
              type="button"
              class="btn btn-sm bg-gradient-info text-white mb-0"
              :disabled="aiLoading || !hasAiSources"
              @click="generateScopeByAi"
            >
              <i class="fas fa-spinner fa-spin me-1" v-if="aiLoading"></i>
              <span v-if="aiLoading">Generating (TH + EN)...</span>
              <span v-else>Generate Scope</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, fetchWithTimeout, AI_FETCH_TIMEOUT_MS } from "@/config/apiBase";
import BaseSelect from "@/components/BaseSelect.vue";
import JiraStatusSelect from "@/components/JiraStatusSelect.vue";
import {
  assignJiraIssue,
  commentJiraIssue,
  enrichJiraLinkRows,
  fetchJiraAssignableUsers,
  fetchJiraIssueDetail,
  fetchJiraIssuesByKeys,
} from "@/services/jiraStatusApi";
import { parseJiraLinksPayload } from "@/utils/jiraLinksPayload";

export default {
  name: "ScopeTab",
  components: { BaseSelect, JiraStatusSelect },
  props: {
    projectPlanId: { type: [String, Number], default: "" },
    subProjectId: { type: [String, Number], default: null },
    subProjectName: { type: String, default: "" },
    initialScope: { type: String, default: "" },
    initialJiraLinks: { type: Array, default: () => [] },
    enableAi: { type: Boolean, default: true },
    readOnly: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  emits: ["jira-links-updated"],
  data() {
    return {
      scopeJiraLinks: [],
      scopeJiraProjectKey: "",
      scopeText: "",
      savedScopeText: "",
      scopeDocuments: { files: [], links: [] },
      documentLinkUrl: "",
      documentUploading: false,
      documentLinkSaving: false,
      documentDeletingId: "",
      docDownloadingId: "",
      docPreview: {
        show: false,
        file: null,
        kind: "binary",
        url: "",
        textContent: "",
        loading: false,
        error: "",
      },
      documentError: null,
      scopeSaving: false,
      scopeSaveError: null,
      showSaveSuccessModal: false,
      showUnsavedModal: false,
      unsavedLeaveResolver: null,
      unsavedSaveError: null,
      showAddJira: false,
      addJiraProjectKey: "",
      addJiraIssueKeys: [],
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      addJiraError: null,
      showJiraViewModal: false,
      jiraViewIssue: null,
      jiraViewLoading: false,
      jiraViewError: null,
      jiraViewComment: "",
      jiraViewAssignableUsers: [],
      jiraViewAssigneeSaving: false,
      jiraViewCommentSaving: false,
      showAiModal: false,
      aiIncludeCurrentScope: false,
      aiIncludeFiles: false,
      aiIncludeLinks: false,
      aiIncludeJira: false,
      aiInputText: "",
      aiInputUrl: "",
      aiLoading: false,
      aiError: null,
      jiraStatusError: null,
    };
  },
  computed: {
    scopeJiraProjectLabel() {
      const key = this.scopeJiraProjectKey;
      if (!key) return "";
      const found = (this.jiraProjects || []).find((p) => p.key === key);
      return found ? `${found.name} (${key})` : key;
    },
    hasCurrentScopeSource() {
      return Boolean(this.scopeText.trim());
    },
    hasFileSource() {
      return this.scopeDocuments.files.length > 0;
    },
    hasLinkSource() {
      return this.scopeDocuments.links.length > 0;
    },
    hasJiraSource() {
      return this.scopeJiraLinks.length > 0;
    },
    hasAiSources() {
      if (this.aiIncludeCurrentScope && this.hasCurrentScopeSource) return true;
      if (this.aiIncludeFiles && this.hasFileSource) return true;
      if (this.aiIncludeLinks && this.hasLinkSource) return true;
      if (this.aiIncludeJira && this.hasJiraSource) return true;
      if (this.aiInputText.trim()) return true;
      if (this.aiInputUrl.trim()) return true;
      return false;
    },
    jiraProjectSelectOptions() {
      return (this.jiraProjects || []).map((p) => ({
        label: `${p.name} (${p.key})`,
        value: p.key,
      }));
    },
    jiraViewAssigneeOptions() {
      const users = Array.isArray(this.jiraViewAssignableUsers) ? this.jiraViewAssignableUsers : [];
      const options = [{ label: "Unassigned", value: "" }];
      const seen = new Set([""]);
      users.forEach((u) => {
        const id = String(u.accountId || "").trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        options.push({
          label: u.displayName || u.emailAddress || id,
          value: id,
        });
      });
      const currentId = String(this.jiraViewIssue?.assigneeAccountId || "").trim();
      if (currentId && !seen.has(currentId)) {
        options.push({
          label: this.jiraViewIssue?.assignee || currentId,
          value: currentId,
        });
      }
      return options;
    },
  },
  watch: {
    subProjectId: {
      immediate: true,
      handler() {
        this.bootstrapFromProps();
        this.loadScope();
      },
    },
    initialJiraLinks: {
      deep: true,
      handler() {
        if (!this.subProjectId) this.scopeJiraLinks = [...this.initialJiraLinks];
      },
    },
  },
  methods: {
    bootstrapFromProps() {
      this.scopeJiraLinks = this.initialJiraLinks.length ? [...this.initialJiraLinks] : [];
      this.scopeText = this.initialScope || "";
      this.savedScopeText = this.scopeText;
      this.enrichJiraLinks();
    },
    async loadScope() {
      if (!this.subProjectId) return;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope`);
        const data = await res.json();
        if (data.error) return;
        this.scopeJiraProjectKey = data.jiraProjectKey || "";
        if (Array.isArray(data.jiraLinks)) {
          this.scopeJiraLinks = data.jiraLinks;
          this.$emit("jira-links-updated", data.jiraLinks);
          this.enrichJiraLinks();
        }
        if (!this.scopeJiraProjectKey && this.scopeJiraLinks.length > 0) {
          this.scopeJiraProjectKey = String(this.scopeJiraLinks[0].key).split("-")[0];
        }
        if (this.scopeJiraProjectKey && this.jiraProjects.length === 0) {
          this.loadJiraProjects();
        }
        if (data.scope !== undefined && data.scope !== null && String(data.scope).trim() !== "") {
          this.scopeText = data.scope || "";
        }
        if (data.scopeDocuments) {
          this.scopeDocuments = {
            files: Array.isArray(data.scopeDocuments.files) ? data.scopeDocuments.files : [],
            links: Array.isArray(data.scopeDocuments.links) ? data.scopeDocuments.links : [],
          };
        }
        this.savedScopeText = this.scopeText;
      } catch (e) {
        console.error("ScopeTab loadScope:", e);
      }
    },
    async enrichJiraLinks() {
      if (!Array.isArray(this.scopeJiraLinks) || this.scopeJiraLinks.length === 0) return;
      this.jiraStatusError = null;
      try {
        const keys = this.scopeJiraLinks.map((j) => j.key).filter(Boolean);
        if (keys.length === 0) return;
        const data = await fetchJiraIssuesByKeys(keys);
        this.scopeJiraLinks = enrichJiraLinkRows(this.scopeJiraLinks, data);
      } catch (e) {
        this.jiraStatusError = e.message || "Cannot load Jira status";
      }
    },
    isScopeDirty() {
      if (!this.subProjectId) return false;
      return this.scopeText !== this.savedScopeText;
    },
    requestTabLeave() {
      if (!this.isScopeDirty()) return Promise.resolve(true);
      return new Promise((resolve) => {
        this.unsavedSaveError = null;
        this.unsavedLeaveResolver = resolve;
        this.showUnsavedModal = true;
      });
    },
    finishUnsavedLeave(allowed) {
      this.showUnsavedModal = false;
      const resolver = this.unsavedLeaveResolver;
      this.unsavedLeaveResolver = null;
      this.unsavedSaveError = null;
      if (resolver) resolver(allowed);
    },
    cancelUnsavedLeave() {
      this.finishUnsavedLeave(false);
    },
    confirmUnsavedLeave() {
      this.finishUnsavedLeave(true);
    },
    async saveAndConfirmLeave() {
      this.unsavedSaveError = null;
      const ok = await this.saveScope({ showSuccessModal: false });
      if (!ok) {
        this.unsavedSaveError = this.scopeSaveError || "Save failed";
        return;
      }
      this.finishUnsavedLeave(true);
    },
    async saveScope(options = {}) {
      const { showSuccessModal = true } = options;
      if (!this.subProjectId) return false;
      this.scopeSaving = true;
      this.scopeSaveError = null;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scope: this.scopeText, actorUserId: this.$store?.state?.user?.userId || null }),
        });
        const json = await res.json();
        if (json.error) throw new Error(json.error);
        this.savedScopeText = this.scopeText;
        if (showSuccessModal) this.showSaveSuccessModal = true;
        return true;
      } catch (e) {
        this.scopeSaveError = e.message;
        return false;
      } finally {
        this.scopeSaving = false;
      }
    },
    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    formatDocDate(value) {
      if (!value) return "";
      try {
        return new Date(value).toLocaleString();
      } catch (_) {
        return "";
      }
    },
    documentPreviewKind(file) {
      const ct = String(file?.contentType || "").toLowerCase();
      const name = String(file?.name || "").toLowerCase();
      const baseName = name.split(/[\\/]/).pop() || name;
      if (ct.includes("pdf") || baseName.endsWith(".pdf")) return "pdf";
      if (ct.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(baseName)) return "image";
      if (ct.includes("json") || baseName.endsWith(".json")) return "json";
      if (
        /\.csv$/i.test(baseName)
        || ct === "text/csv"
        || ct === "application/csv"
        || ct === "text/comma-separated-values"
        || (ct === "application/vnd.ms-excel" && /\.csv$/i.test(baseName))
      ) {
        return "csv";
      }
      if (ct.startsWith("text/") || /\.(txt|md|log|xml|html?|yml|yaml)$/.test(baseName)) return "text";
      return "binary";
    },
    documentFileIcon(file) {
      const kind = this.documentPreviewKind(file);
      if (kind === "pdf") return "fas fa-file-pdf text-danger";
      if (kind === "image") return "fas fa-file-image text-info";
      if (kind === "json") return "fas fa-file-code text-warning";
      if (kind === "csv") return "fas fa-file-excel text-success";
      if (kind === "text") return "fas fa-file-alt text-secondary";
      const name = String(file?.name || "").toLowerCase();
      if (/\.(xlsx?|csv)$/.test(name)) return "fas fa-file-excel text-success";
      if (/\.(docx?|pptx?)$/.test(name)) return "fas fa-file-word text-primary";
      return "fas fa-file text-secondary";
    },
    closeDocPreview() {
      this.docPreview.show = false;
      this.docPreview.file = null;
      this.docPreview.url = "";
      this.docPreview.textContent = "";
      this.docPreview.error = "";
      this.docPreview.loading = false;
    },
    openDocumentInNewTab(file) {
      if (!file?.url) return;
      window.open(file.url, "_blank", "noopener,noreferrer");
    },
    scopeDocumentContentUrl(file, download = false) {
      const qs = download ? "?download=1" : "";
      return `${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope/document/${file.id}/content${qs}`;
    },
    isTextPreviewKind(kind) {
      return kind === "text" || kind === "json" || kind === "csv";
    },
    async openDocumentPreview(file) {
      if (!file?.id || !this.subProjectId) return;
      const kind = this.documentPreviewKind(file);
      this.docPreview = {
        show: true,
        file,
        kind,
        url: file.url,
        textContent: "",
        loading: this.isTextPreviewKind(kind),
        error: "",
      };
      if (!this.isTextPreviewKind(kind)) return;
      try {
        const res = await fetchWithTimeout(this.scopeDocumentContentUrl(file));
        if (!res.ok) {
          const json = await this.parseApiJson(res);
          throw new Error(json.error || "Cannot load file preview");
        }
        const text = await res.text();
        if (kind === "json") {
          try {
            this.docPreview.textContent = JSON.stringify(JSON.parse(text), null, 2);
          } catch (_) {
            this.docPreview.textContent = text;
          }
        } else {
          this.docPreview.textContent = text;
        }
      } catch (err) {
        this.docPreview.error = err.message || "Cannot load preview";
      } finally {
        this.docPreview.loading = false;
      }
    },
    async downloadDocumentFile(file) {
      if (!file?.id || !this.subProjectId) return;
      this.docDownloadingId = file.id;
      this.documentError = null;
      try {
        const res = await fetchWithTimeout(this.scopeDocumentContentUrl(file, true));
        if (!res.ok) {
          const json = await this.parseApiJson(res);
          throw new Error(json.error || "Download failed");
        }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = file.name || "document";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        this.documentError = err.message || "Download failed";
        this.openDocumentInNewTab(file);
      } finally {
        this.docDownloadingId = "";
      }
    },
    async parseApiJson(res) {
      const text = await res.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (_) {
        if (text.trimStart().startsWith("<")) {
          throw new Error("Invalid API response — check backend / proxy configuration.");
        }
        throw new Error(text.slice(0, 200) || "Invalid API response");
      }
    },
    async uploadDocumentFile(e) {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !this.subProjectId) return;
      if (file.size > 50 * 1024 * 1024) {
        this.documentError = "Max file size is 50 MB.";
        return;
      }
      this.documentUploading = true;
      this.documentError = null;
      try {
        const dataBase64 = await this.readFileAsBase64(file);
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope/document-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type || "application/octet-stream",
            dataBase64,
          }),
        });
        const json = await this.parseApiJson(res);
        if (!res.ok || json.error) throw new Error(json.error || "Upload failed");
        if (json.documents) this.scopeDocuments = json.documents;
      } catch (err) {
        this.documentError = err.message || "Upload failed";
      } finally {
        this.documentUploading = false;
      }
    },
    async saveDocumentLink() {
      if (!this.subProjectId || !this.documentLinkUrl.trim()) return;
      this.documentLinkSaving = true;
      this.documentError = null;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope/document-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: this.documentLinkUrl.trim() }),
        });
        const json = await this.parseApiJson(res);
        if (!res.ok || json.error) throw new Error(json.error || "Cannot save link");
        if (json.documents) this.scopeDocuments = json.documents;
        this.documentLinkUrl = "";
      } catch (err) {
        this.documentError = err.message || "Cannot save link";
      } finally {
        this.documentLinkSaving = false;
      }
    },
    async removeDocument(type, id) {
      if (!this.subProjectId || !id) return;
      this.documentDeletingId = id;
      this.documentError = null;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/scope/document`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type, id }),
        });
        const json = await this.parseApiJson(res);
        if (!res.ok || json.error) throw new Error(json.error || "Cannot remove document");
        if (json.documents) this.scopeDocuments = json.documents;
      } catch (err) {
        this.documentError = err.message || "Cannot remove document";
      } finally {
        this.documentDeletingId = "";
      }
    },
    async persistScopeJiraLinks(links, projectKey) {
      const res = await fetchWithTimeout(`${API_BASE_URL}/api/sub-projects/${this.subProjectId}/jira-links`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jiraLinks: links,
          jiraProjectKey: projectKey || null,
          actorUserId: this.$store?.state?.user?.userId || null,
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
    },
    async addScopeJiraLink() {
      const keys = [...this.addJiraIssueKeys];
      const projectKey = this.addJiraProjectKey || this.scopeJiraProjectKey || "";
      if (keys.length === 0 && !projectKey) {
        this.closeAddJiraModal();
        return;
      }
      const base = "https://lightnet-thailand.atlassian.net/browse";
      let links = [...this.scopeJiraLinks];
      if (keys.length > 0) {
        const newLinks = keys
          .filter((k) => !links.some((j) => j.key === k))
          .map((k) => ({ key: k, url: `${base}/${k}` }));
        links = [...links, ...newLinks];
      }
      this.addJiraError = null;
      try {
        await this.persistScopeJiraLinks(links, projectKey);
        this.scopeJiraLinks = links;
        this.scopeJiraProjectKey = projectKey;
        this.closeAddJiraModal();
        this.$emit("jira-links-updated", links);
        if (links.length > 0) this.enrichJiraLinks();
      } catch (e) {
        this.addJiraError = e.message;
      }
    },
    openAddJiraModal() {
      this.addJiraError = null;
      this.addJiraProjectKey = this.scopeJiraProjectKey || "";
      this.addJiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
      this.showAddJira = true;
      this.loadJiraProjects();
      if (this.addJiraProjectKey) this.loadJiraIssues(this.addJiraProjectKey);
    },
    closeAddJiraModal() {
      this.showAddJira = false;
      this.addJiraError = null;
      this.addJiraProjectKey = "";
      this.addJiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
    },
    async loadJiraProjects() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) {
        console.error("loadJiraProjects error:", e);
      } finally {
        this.jiraLoadingProjects = false;
      }
    },
    async loadJiraIssues(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) {
        console.error("loadJiraIssues error:", e);
      } finally {
        this.jiraLoadingIssues = false;
      }
    },
    onAddJiraProjectChange() {
      this.addJiraIssueKeys = [];
      this.jiraIssues = [];
      this.jiraSearch = "";
      if (this.addJiraProjectKey) this.loadJiraIssues(this.addJiraProjectKey);
    },
    toggleAddJiraTicket(key) {
      const idx = this.addJiraIssueKeys.indexOf(key);
      if (idx === -1) this.addJiraIssueKeys.push(key);
      else this.addJiraIssueKeys.splice(idx, 1);
    },
    removeAddJiraTicket(key) {
      this.addJiraIssueKeys = this.addJiraIssueKeys.filter((k) => k !== key);
    },
    onJiraSearch() {
      clearTimeout(this.jiraSearchTimer);
      this.jiraSearchTimer = setTimeout(() => {
        if (this.addJiraProjectKey) this.loadJiraIssues(this.addJiraProjectKey, this.jiraSearch);
      }, 400);
    },
    async openJiraView(link) {
      const key = String(link?.key || "").trim();
      if (!key) return;
      this.jiraViewError = null;
      this.jiraViewComment = "";
      this.jiraViewAssignableUsers = [];
      this.jiraViewIssue = {
        key,
        summary: link.summary || "",
        status: link.status || "",
        issueType: "",
        projectKey: "",
        assignee: "",
        assigneeAccountId: "",
        description: "",
        comments: [],
        transitions: link.transitions || [],
      };
      this.showJiraViewModal = true;
      this.jiraViewLoading = true;
      try {
        const data = await fetchJiraIssueDetail(key);
        this.jiraViewIssue = {
          key: data.key || key,
          summary: data.summary || link.summary || "",
          status: data.status || link.status || "",
          issueType: data.issueType || "",
          projectKey: data.projectKey || "",
          assignee: data.assignee || "",
          assigneeAccountId: data.assigneeAccountId || "",
          description: data.description || "",
          comments: Array.isArray(data.comments) ? data.comments : [],
          transitions: Array.isArray(data.transitions) ? data.transitions : link.transitions || [],
        };
        await this.loadJiraViewAssignable(key, data.projectKey);
      } catch (e) {
        this.jiraViewError = e.message || "Failed to load Jira issue";
      } finally {
        this.jiraViewLoading = false;
      }
    },
    async loadJiraViewAssignable(issueKey, projectKey) {
      try {
        this.jiraViewAssignableUsers = await fetchJiraAssignableUsers({
          issueKey,
          projectKey: projectKey || this.scopeJiraProjectKey,
        });
      } catch (_) {
        this.jiraViewAssignableUsers = [];
      }
    },
    closeJiraView() {
      this.showJiraViewModal = false;
      this.jiraViewIssue = null;
      this.jiraViewLoading = false;
      this.jiraViewError = null;
      this.jiraViewComment = "";
      this.jiraViewAssignableUsers = [];
      this.jiraViewAssigneeSaving = false;
      this.jiraViewCommentSaving = false;
    },
    async onJiraViewAssigneeChange(accountId) {
      const key = String(this.jiraViewIssue?.key || "").trim();
      if (!key || this.readOnly || this.jiraViewAssigneeSaving) return;
      const nextId = accountId == null ? "" : String(accountId);
      if (nextId === String(this.jiraViewIssue.assigneeAccountId || "")) return;
      this.jiraViewAssigneeSaving = true;
      this.jiraViewError = null;
      try {
        const data = await assignJiraIssue(key, nextId || null);
        if (this.jiraViewIssue) {
          this.jiraViewIssue = {
            ...this.jiraViewIssue,
            assignee: data.assignee || "",
            assigneeAccountId: data.assigneeAccountId || "",
          };
        }
      } catch (e) {
        this.jiraViewError = e.message || "Failed to update Jira assignee";
      } finally {
        this.jiraViewAssigneeSaving = false;
      }
    },
    async submitJiraViewComment() {
      const key = String(this.jiraViewIssue?.key || "").trim();
      const body = String(this.jiraViewComment || "").trim();
      if (!key || !body || this.readOnly || this.jiraViewCommentSaving) return;
      this.jiraViewCommentSaving = true;
      this.jiraViewError = null;
      try {
        const created = await commentJiraIssue(key, body);
        if (this.jiraViewIssue) {
          const comments = [...(this.jiraViewIssue.comments || []), created];
          this.jiraViewIssue = { ...this.jiraViewIssue, comments };
        }
        this.jiraViewComment = "";
      } catch (e) {
        this.jiraViewError = e.message || "Failed to add Jira comment";
      } finally {
        this.jiraViewCommentSaving = false;
      }
    },
    onJiraViewStatusUpdate(status) {
      const next = status || "";
      if (this.jiraViewIssue) this.jiraViewIssue = { ...this.jiraViewIssue, status: next };
      const key = String(this.jiraViewIssue?.key || "").toUpperCase();
      this.scopeJiraLinks = this.scopeJiraLinks.map((row) => (
        String(row.key || "").toUpperCase() === key ? { ...row, status: next } : row
      ));
    },
    onJiraViewTransitionsUpdate(transitions) {
      const next = Array.isArray(transitions) ? transitions : [];
      if (this.jiraViewIssue) this.jiraViewIssue = { ...this.jiraViewIssue, transitions: next };
      const key = String(this.jiraViewIssue?.key || "").toUpperCase();
      this.scopeJiraLinks = this.scopeJiraLinks.map((row) => (
        String(row.key || "").toUpperCase() === key ? { ...row, transitions: next } : row
      ));
    },
    onJiraViewError(message) {
      this.jiraViewError = message || "Failed to update Jira status";
    },
    formatJiraCommentTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async removeScopeJiraLink(key) {
      const updated = this.scopeJiraLinks.filter((j) => j.key !== key);
      try {
        await this.persistScopeJiraLinks(updated, this.scopeJiraProjectKey);
        this.scopeJiraLinks = updated;
        this.$emit("jira-links-updated", updated);
      } catch (e) {
        console.error("removeScopeJiraLink:", e);
      }
    },
    openAiModal() {
      this.aiError = null;
      this.aiIncludeCurrentScope = this.hasCurrentScopeSource;
      this.aiIncludeFiles = this.hasFileSource;
      this.aiIncludeLinks = this.hasLinkSource;
      this.aiIncludeJira = this.hasJiraSource;
      this.showAiModal = true;
    },
    async generateScopeByAi() {
      this.aiError = null;
      if (!this.hasAiSources) {
        this.aiError = "Add documents, links, Jira tickets, scope text, or requirements in the modal.";
        return;
      }
      this.aiLoading = true;
      try {
        const useFullContext = Boolean(this.subProjectId);
        const endpoint = useFullContext
          ? `${API_BASE_URL}/api/ai/generate-test-scope`
          : `${API_BASE_URL}/api/ai/analyze-requirements`;
        const body = useFullContext
          ? {
              subProjectId: this.subProjectId,
              language: "both",
              existingScope: this.aiIncludeCurrentScope ? this.scopeText : "",
              includeSources: {
                existingScope: this.aiIncludeCurrentScope,
                files: this.aiIncludeFiles,
                links: this.aiIncludeLinks,
                jira: this.aiIncludeJira,
              },
              text: this.aiInputText.trim() || undefined,
              url: this.aiInputUrl.trim() || undefined,
            }
          : {
              language: "both",
              text: this.aiInputText.trim() || undefined,
              url: this.aiInputUrl.trim() || undefined,
            };
        if (!useFullContext && !body.text && !body.url) {
          this.aiError = "Please provide requirement text or URL.";
          this.aiLoading = false;
          return;
        }
        const res = await fetchWithTimeout(
          endpoint,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
          AI_FETCH_TIMEOUT_MS,
        );
        const json = await this.parseApiJson(res);
        if (!res.ok || json.error) throw new Error(json.error || "AI generation failed");
        this.scopeText = json.scope || "";
        if (json.documents) this.scopeDocuments = json.documents;
        this.showAiModal = false;
        this.aiInputText = "";
        this.aiInputUrl = "";
      } catch (e) {
        this.aiError = e.message;
      } finally {
        this.aiLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.scope-ai-sources {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
}

.scope-ai-source-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

.scope-ai-source-list li + li {
  margin-top: 0.25rem;
}

.scope-ai-source-item {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0;
  cursor: pointer;
  font-weight: 500;
  color: #344767;
}

.scope-ai-source-item input {
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.scope-ai-source-item.is-disabled {
  cursor: not-allowed;
  color: #8392ab;
  font-weight: 400;
}

.scope-tab-shell {
  flex: 1;
  min-height: 0;
}

.scope-tab-inner {
  min-height: min-content;
}

.scope-doc-link-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
}

.scope-doc-link-input {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
}

.scope-doc-action-btn {
  white-space: nowrap;
  flex-shrink: 0;
}

.scope-doc-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.scope-doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-height: 2rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
}

.scope-doc-item-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.scope-doc-type-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  flex-shrink: 0;
  font-size: 1rem;
  line-height: 1;
}

.scope-doc-date {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: 0.75rem;
  line-height: 1;
  margin-right: 0.15rem;
}

.scope-doc-item-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;
}

.scope-doc-item-actions .btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0;
  width: 1.125rem;
  height: 1.125rem;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1 !important;
  border: none;
  box-shadow: none !important;
  vertical-align: middle;
}

.scope-doc-item-actions .scope-doc-icon-btn {
  color: #5e72e4;
}

.scope-doc-item-actions .scope-doc-icon-btn i,
.scope-doc-item-actions .action-delete i {
  font-size: 0.875rem !important;
  line-height: 1 !important;
  display: block;
  width: auto;
  height: auto;
}

.scope-doc-item-actions .scope-doc-icon-btn:hover {
  color: #324cdd;
}

.scope-doc-preview-box {
  width: min(960px, 96vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scope-doc-preview-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: #f8f9fa;
}

.scope-doc-preview-frame {
  display: block;
  width: 100%;
  height: 70vh;
  border: none;
  background: #fff;
}

.scope-doc-preview-image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  min-height: 240px;
}

.scope-doc-preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  background: #fff;
  border-radius: 8px;
}

.scope-doc-preview-text {
  margin: 0;
  padding: 1rem;
  font-size: 0.8rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: #fff;
  min-height: 240px;
}

.scope-doc-name {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.25;
  color: #344767;
  text-decoration: none;
  word-break: break-word;
}

.scope-doc-name:hover {
  color: #5e72e4;
  text-decoration: underline;
}

.jira-chip {
  background: #f0f4ff;
  border: 1px solid #c5d0f5;
  border-radius: 8px;
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
}
.jira-chip-remove {
  cursor: pointer;
  color: #8392ab;
  font-weight: 700;
  margin-left: 0.25rem;
}
.jira-chip-remove:hover {
  color: #e74c3c;
}
.scope-confirm-modal {
  width: min(480px, 94vw);
}

.scope-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1rem 0;
}

.scope-modal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.scope-modal-close-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  color: #8392ab;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}

.scope-modal-close-x:hover {
  color: #344767;
  border-color: #cbd3da;
  background: #f8f9fa;
}

.scope-modal-close-x i {
  font-size: 0.875rem;
}

.scope-modal-actions .btn {
  white-space: nowrap;
  flex-shrink: 0;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  max-width: 100%;
}
.jira-links-table th,
.jira-links-table td {
  vertical-align: middle;
}
.jira-id-cell {
  width: 124px;
  padding-right: 0.35rem !important;
}
.jira-title-col {
  width: auto;
}
.jira-title-cell {
  padding-left: 0.2rem !important;
  padding-right: 0.35rem !important;
}
.jira-status-col {
  width: 170px;
}
.jira-status-cell {
  padding-left: 0.25rem !important;
  padding-right: 0.25rem !important;
  overflow: visible;
  position: relative;
}
.jira-action-col {
  width: 72px;
}
.action-view {
  color: #5e72e4;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.action-view i,
.action-delete i {
  font-size: 0.875rem !important;
  width: 0.875rem;
  height: 0.875rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1;
}
.action-view:hover {
  color: #324cdd;
}
.scope-jira-view-box {
  width: 920px;
  max-width: 96vw;
  height: 86vh;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.scope-jira-view-box > .border-bottom,
.scope-jira-view-box > .border-top {
  flex-shrink: 0;
}
.scope-jira-view-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.scope-jira-view-desc {
  min-height: 8rem;
  max-height: 28vh;
  overflow-y: auto;
  padding: 0.55rem 0.7rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
  font-size: 0.8rem;
  color: #344767;
  white-space: pre-wrap;
  word-break: break-word;
}
.scope-jira-view-empty {
  padding: 0.7rem 0.75rem;
  border: 1px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}
.scope-jira-view-comments {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 32vh;
  overflow-y: auto;
}
.scope-jira-view-comment {
  padding: 0.65rem 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  font-size: 0.78rem;
  color: #344767;
}
.scope-jira-view-comment-body {
  white-space: pre-wrap;
  word-break: break-word;
  color: #495057;
}
.scope-jira-view-composer textarea {
  resize: vertical;
  min-height: 4.5rem;
}
.jira-action-cell {
  text-align: center;
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.action-delete {
  color: #e74c3c;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.action-delete:hover {
  color: #c0392b;
}
.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}
.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}
.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.jira-issue-item:last-child {
  border-bottom: none;
}
.jira-issue-item:hover {
  background: #f8f9fa;
}
.jira-issue-selected {
  background: #e8f4fd;
}

.scope-tab-embedded {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.scope-tab-embedded .scope-tab-inner {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
